import React, { useState } from 'react'
import AddIcon from '@mui/icons-material/Add'

import Avatar from 'components/Common/Avatar'
import Button from 'components/Common/Button'
import ErrorMessage from 'components/Common/ErrorMessage'

import {
  cypressAddPictureButton,
  cypressAddPictureInput,
} from 'constants/cypress'
import { maxAllowedSizePerFileInKb } from 'constants/fileSizes'
import { textFiles } from 'constants/textFiles'
import useTranslation from 'hooks/useTranslation'

import { colors, FONT_WEIGHT } from 'styles/theme'

import { StyledContainer } from '../style'

type AvatarProps = {
  title: string
  description?: string
  picture: string
  handlePictureChange: React.Dispatch<React.SetStateAction<string>>
  handleFileChange?: React.Dispatch<React.SetStateAction<File | null>>
  handleFileStateChange?: (file: File) => void
  size?: 'small' | 'medium' | 'large'
  buttonText?: string
  disabled?: boolean
  id?: string
  additionalButtonStyles?: React.CSSProperties
}

/*
This component is used in any of the information step of a setting creation process, 
includes a container with text adapted to its name and an avatar input
*/

const AvatarContainer = ({
  title,
  description,
  picture,
  handlePictureChange,
  handleFileChange,
  handleFileStateChange,
  size,
  buttonText,
  disabled,
  id = 'profile-picture-input',
  additionalButtonStyles,
}: AvatarProps) => {
  const [fileSizeError, setFileSizeError] = useState<boolean>(false)
  const { text: translation } = useTranslation(textFiles.BRAND_CREATION)
  const { text: generalText } = useTranslation(textFiles.GENERAL)

  const { buttons } = translation

  const imageHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const reader = new FileReader()
    const { files } = e.target
    const file = files && files.length > 0 ? files[0] : null

    if (file && file.size <= maxAllowedSizePerFileInKb) {
      setFileSizeError(false)
      reader.onload = () => {
        if (reader.readyState === 2) {
          handlePictureChange(String(reader.result))
        }
      }
      if (file) {
        if (handleFileChange) handleFileChange(file)
        if (handleFileStateChange) handleFileStateChange(file)
        reader.readAsDataURL(file)
      }
    } else if (file && file.size > maxAllowedSizePerFileInKb) {
      setFileSizeError(true)
    }
  }

  return (
    <>
      <StyledContainer
        title={title}
        description={description}
        contentStyle={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
        sx={{
          ...additionalButtonStyles,
        }}
      >
        <Button
          variant="text"
          role="button"
          sx={{
            fontWeight: FONT_WEIGHT.MEDIUM,
            border: `2px solid ${
              !disabled ? colors.blue : colors.gray
            } !important`,
            padding: '0px !important',
            width: 'auto',
            ...additionalButtonStyles,
          }}
          size={size}
          disabled={disabled}
          testId={cypressAddPictureButton}
        >
          <label
            htmlFor={id}
            style={{
              cursor: 'pointer',
              height: '100%',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              margin: '0 10px',
            }}
          >
            <AddIcon />
            {buttonText || buttons.file}
          </label>
        </Button>
        <input
          type="file"
          name="input"
          id={id}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={imageHandler}
          disabled={disabled}
          data-cy={cypressAddPictureInput}
        />
        {picture ? (
          <div style={{ marginTop: '1rem' }}>
            <Avatar height={95} width={95} variant="rounded" image={picture} />
          </div>
        ) : null}
      </StyledContainer>
      {fileSizeError ? (
        <ErrorMessage
          text={generalText.fileSize.sizeError.replace('%d', 3)}
          sx={{ marginTop: '5px' }}
        />
      ) : null}
    </>
  )
}

export default AvatarContainer
